import React, { useState } from 'react';
import { MarketplaceEvalResponse, RateLimitError } from '../types';
import { auth } from '../config/firebase';
import AuthenticatedImage from './AuthenticatedImage';
import RateLimitModal from './RateLimitModal';

const SLIDE_API_BASE_URL = process.env.REACT_APP_SLIDE_API_BASE_URL || 'https://slide.dev.carouselshopai.com';

interface MPESearchModalProps {
  analysisId?: string;  // For Quick Analyze workflow
  slideId?: string;     // For existing Slide workflow
  shopId: string;
  suggestedQuery: string;
  imageUrls?: string[];  // v17: Array of image URLs (blob URLs for Quick Analyze, ignored for Slide workflow)
  slideImages?: Array<{ id: string }>;  // For Slide workflow: array of image objects with IDs
  onClose: () => void;
  onResults: (response: MarketplaceEvalResponse) => void;
}

const MPESearchModal: React.FC<MPESearchModalProps> = ({
  analysisId,
  slideId,
  shopId,
  suggestedQuery,
  imageUrls,
  slideImages,
  onClose,
  onResults
}) => {
  const [searchQuery, setSearchQuery] = useState(suggestedQuery || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rateLimitError, setRateLimitError] = useState<RateLimitError | null>(null);
  // v17: Default to first image (index 0) if images are provided
  const hasImages = (imageUrls && imageUrls.length > 0) || (slideImages && slideImages.length > 0);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(
    hasImages ? 0 : null
  );

  const runMarketplaceEval = async () => {
    setLoading(true);
    setError(null);

    try {
      const idToken = await auth.currentUser?.getIdToken();

      // Determine which workflow we're in and construct appropriate request
      let apiUrl: string;
      let requestBody: any;

      if (slideId) {
        // Existing Slide workflow
        apiUrl = `${SLIDE_API_BASE_URL}/api/v1/shops/${shopId}/slides/${slideId}/marketplace-eval`;
        requestBody = {
          searchQuery: searchQuery.trim()
        };
        // Include image index if selected
        if (selectedImageIndex !== undefined && selectedImageIndex !== null) {
          requestBody.imageIndex = selectedImageIndex;
        }
      } else if (analysisId) {
        // Quick Analyze workflow
        apiUrl = `${SLIDE_API_BASE_URL}/api/v1/shops/${shopId}/marketplace-eval`;
        requestBody = {
          analysisId: analysisId,
          searchQuery: searchQuery.trim()
        };
        // Include image index if selected
        if (selectedImageIndex !== undefined && selectedImageIndex !== null) {
          requestBody.imageIndex = selectedImageIndex;
        }
      } else {
        throw new Error('Either analysisId or slideId must be provided');
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        if (response.status === 403) {
          const errorData = await response.json().catch(() => null);
          throw new Error(errorData?.error || 'You do not have permission to run Marketplace Evals.');
        } else if (response.status === 429) {
          // Parse rate limit error response and show modal
          const errorData = await response.json();
          const rateLimitErr = new RateLimitError({
            resourceType: errorData.resourceType || 'MARKETPLACE_EVALS',
            currentUsage: errorData.currentUsage || 0,
            maxAllowed: errorData.maxAllowed || 0,
            tierType: errorData.tierType || 'UNKNOWN',
            message: errorData.rateLimitMessage || 'Rate limit exceeded'
          });
          setRateLimitError(rateLimitErr);
          setLoading(false);
          return; // Don't throw - modal will handle display
        } else {
          throw new Error('Failed to run Marketplace Eval. Please try again.');
        }
      }

      const data: MarketplaceEvalResponse = await response.json();
      onResults(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to run Marketplace Eval. Please try again.';
      setError(errorMessage);
      console.error('Marketplace Eval error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    // Cmd/Ctrl + Enter to submit (allow plain Enter for new lines in textarea)
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !loading) {
      e.preventDefault();
      runMarketplaceEval();
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '16px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'var(--bg-card)',
          padding: '24px',
          borderRadius: '8px',
          maxWidth: '800px',
          width: '100%',
          maxHeight: '80vh',
          overflowY: 'auto',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{
          fontSize: '1.5rem',
          fontWeight: 600,
          color: 'var(--text-primary)',
          marginBottom: '8px'
        }}>
          Run Marketplace Eval
        </h2>

        <p style={{
          color: 'var(--text-secondary)',
          fontSize: '0.875rem',
          marginBottom: '20px',
          lineHeight: '1.5'
        }}>
          Search online marketplaces to see similar items and pricing.
          Edit the search query below if needed.
        </p>

        {/* v17: Image Selection Grid */}
        {hasImages && (
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{
              fontSize: '1rem',
              fontWeight: 600,
              color: 'var(--text-primary)',
              marginBottom: '8px'
            }}>
              Analyzed Images
            </h3>
            <p style={{
              fontSize: '0.75rem',
              color: 'var(--text-secondary)',
              marginBottom: '12px',
              fontStyle: 'italic'
            }}>
              Select the image for Google Lens search.
            </p>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
              gap: '12px'
            }}>
              {/* Render slide images with AuthenticatedImage */}
              {slideImages && slideImages.map((img, index) => (
                <div
                  key={img.id}
                  onClick={() => setSelectedImageIndex(selectedImageIndex === index ? null : index)}
                  style={{
                    position: 'relative',
                    cursor: 'pointer',
                    paddingBottom: '100%',
                    overflow: 'hidden',
                    borderRadius: '6px',
                    border: selectedImageIndex === index ? '3px solid var(--primary-light)' : '2px solid var(--border-light)',
                    transition: 'border 0.2s'
                  }}
                >
                  <AuthenticatedImage
                    shopId={shopId}
                    slideId={slideId!}
                    imageId={img.id}
                    alt={`Image ${index + 1}`}
                    size="thumb_300"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                  {/* Selection indicator checkmark */}
                  {selectedImageIndex === index && (
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      backgroundColor: 'var(--primary)',
                      color: 'var(--text-inverse)',
                      borderRadius: '50%',
                      width: '28px',
                      height: '28px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}>
                      ✓
                    </div>
                  )}
                </div>
              ))}
              {/* Render Quick Analyze blob URLs with regular img */}
              {imageUrls && imageUrls.map((url, index) => (
                <div
                  key={index}
                  onClick={() => setSelectedImageIndex(selectedImageIndex === index ? null : index)}
                  style={{
                    position: 'relative',
                    cursor: 'pointer',
                    paddingBottom: '100%',
                    overflow: 'hidden',
                    borderRadius: '6px',
                    border: selectedImageIndex === index ? '3px solid var(--primary-light)' : '2px solid var(--border-light)',
                    transition: 'border 0.2s'
                  }}
                >
                  <img
                    src={url}
                    alt={`Image ${index + 1}`}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                  {/* Selection indicator checkmark */}
                  {selectedImageIndex === index && (
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      backgroundColor: 'var(--primary)',
                      color: 'var(--text-inverse)',
                      borderRadius: '50%',
                      width: '28px',
                      height: '28px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}>
                      ✓
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <label
          htmlFor="mpe-search-query"
          style={{
            display: 'block',
            marginBottom: '8px',
            fontWeight: 500,
            fontSize: '0.875rem',
            color: 'var(--text-primary)'
          }}
        >
          Search Query (Optional):
        </label>

        <textarea
          id="mpe-search-query"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Enter search terms... (Cmd/Ctrl+Enter to search)"
          disabled={loading}
          rows={4}
          style={{
            width: '100%',
            padding: '12px',
            border: '1px solid var(--border-light)',
            borderRadius: '6px',
            fontSize: '14px',
            marginBottom: '16px',
            boxSizing: 'border-box',
            opacity: loading ? 0.6 : 1,
            fontFamily: 'inherit',
            resize: 'vertical',
            lineHeight: '1.5'
          }}
        />

        {error && (
          <div style={{
            backgroundColor: 'var(--error-bg)',
            border: '1px solid var(--error-border)',
            borderRadius: '4px',
            padding: '12px',
            marginBottom: '16px',
            color: 'var(--error-text)',
            fontSize: '0.875rem'
          }}>
            {error}
          </div>
        )}

        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClose}
            disabled={loading}
            style={{
              backgroundColor: 'var(--border-light)',
              color: 'var(--text-primary)',
              padding: '10px 20px',
              borderRadius: '6px',
              border: 'none',
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
              fontSize: '0.875rem'
            }}
          >
            Cancel
          </button>

          <button
            onClick={runMarketplaceEval}
            disabled={loading}
            style={{
              backgroundColor: 'var(--primary)',
              color: 'var(--text-inverse)',
              padding: '10px 20px',
              borderRadius: '6px',
              border: 'none',
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
              fontSize: '0.875rem'
            }}
          >
            {loading ? 'Searching...' : 'Search Marketplaces'}
          </button>
        </div>
      </div>

      {/* Rate Limit Modal */}
      {rateLimitError && (
        <RateLimitModal
          error={rateLimitError}
          onClose={() => setRateLimitError(null)}
        />
      )}
    </div>
  );
};

export default MPESearchModal;
